import {Root, Icon, Portal, Viewport, ItemText, Separator, Group} from '@radix-ui/react-select'
import emojiFlags from 'emoji-flags'
import type {CountryCode} from 'libphonenumber-js'
import {getCountryCallingCode, AsYouType, parsePhoneNumberFromString} from 'libphonenumber-js'
import React from 'react'
import styled, {css} from 'styled-components'

import type {Props as InputProps} from '@/components/input'
import Input from '@/components/input'
import {SelectWrapper, SelectTrigger, SelectContent, SelectOption, Arrow} from '@/components/select'
import {Text, text} from '@/components/text'
import useCountryList from '@/hooks/use-country-list'
import locales from '@/locale/locales'
import type {InputStylesProps} from '@/styles/inputs'
import {Label} from '@/styles/inputs'
import {ellipsisStyles, focusStyles} from '@/styles/utils'
import {colors, zIndex, tokens} from '@/theme'

interface Props extends Omit<InputProps, 'label' | 'onChange' | 'copyable'> {
    setCountryCode: (newValue: CountryCode) => void
    countryCode: CountryCode
    label?: string
    phoneNumber: string
    required?: boolean
    setPhoneNumber: (phoneNumber: string) => void
    'data-testid'?: string
}

const PhoneInput = ({
    setCountryCode,
    countryCode,
    label,
    phoneNumber,
    setPhoneNumber,
    error,
    required,
    ...props
}: Props) => {
    const countryList = useCountryList()
    const inputId = React.useId()
    const phoneInputRef = React.useRef<HTMLDivElement>(null)
    const [phoneInputWidth, setPhoneInputWidth] = React.useState(0)

    const countryGroups: Array<typeof countryList> = [[], []]

    countryList.forEach((country) => {
        const isSupportedMarket = locales.some(
            (locale) => locale.country.toUpperCase() === country.code.toUpperCase(),
        )

        if (isSupportedMarket) {
            countryGroups[0].push(country)
        } else {
            countryGroups[1].push(country)
        }
    })

    const handlePhoneNumberChange = (value: string) => {
        const formattedNumber = new AsYouType(countryCode).input(value)

        setPhoneNumber(formattedNumber)
    }

    const handlePhoneCountryCodeChange = (countryCode: CountryCode) => {
        const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber, countryCode)

        setCountryCode(countryCode)

        if (parsedPhoneNumber) {
            setPhoneNumber(parsedPhoneNumber.formatNational())
        }
    }

    React.useLayoutEffect(() => {
        if (phoneInputRef.current) {
            setPhoneInputWidth(phoneInputRef.current.offsetWidth)
        }
    }, [])

    return (
        <SelectWrapper ref={phoneInputRef}>
            {label && (
                <Label htmlFor={inputId} $required={required}>
                    {label}
                </Label>
            )}

            <Root value={countryCode} onValueChange={handlePhoneCountryCodeChange}>
                <ListboxButtonWrapper>
                    <CountrySelectButton
                        $hasError={!!error}
                        aria-label={countryCode}
                        className={props.className}
                    >
                        {!!countryCode && (
                            <>
                                <Flag
                                    role="img"
                                    aria-label={emojiFlags.countryCode(countryCode).name}
                                >
                                    {emojiFlags.countryCode(countryCode).emoji}
                                </Flag>
                                <Text translate="no" as="span" $variant="label-base" $color="muted">
                                    +{getCountryCallingCode(countryCode)}
                                </Text>
                            </>
                        )}

                        <Icon>
                            <Arrow />
                        </Icon>
                    </CountrySelectButton>

                    <TextInput
                        id={inputId}
                        type="tel"
                        $hasError={!!error}
                        value={phoneNumber}
                        onChange={handlePhoneNumberChange}
                        required={required}
                        {...props}
                    />
                </ListboxButtonWrapper>

                <Portal>
                    <SelectContent
                        position="popper"
                        style={{width: phoneInputWidth}}
                        sideOffset={2}
                    >
                        <Viewport>
                            {countryGroups.map((group, i) => (
                                <React.Fragment key={i}>
                                    <Group key={i}>
                                        {group.map((country) => (
                                            <CountryOption key={country.code} value={country.code}>
                                                <Flag role="img" aria-label={country.name}>
                                                    {emojiFlags.countryCode(country.code).emoji}
                                                </Flag>

                                                <OptionCountryName>
                                                    {country.name}
                                                </OptionCountryName>

                                                <CallingCode translate="no">
                                                    +{getCountryCallingCode(country.code)}
                                                </CallingCode>
                                            </CountryOption>
                                        ))}
                                    </Group>
                                    <SelectSeparator />
                                </React.Fragment>
                            ))}
                        </Viewport>
                    </SelectContent>
                </Portal>
            </Root>

            {error && (
                <Text
                    $variant="label-base"
                    $color="error"
                    {...(props['data-testid'] && {'data-testid': `${props['data-testid']}-error`})}
                >
                    {error}
                </Text>
            )}
        </SelectWrapper>
    )
}

const ListboxButtonWrapper = styled.div`
    display: grid;
    grid-template-columns: auto 1fr;

    [data-state='expanded'] & {
        column-gap: ${tokens.spacing2};
    }
`

const TextInput = styled(Input)<InputStylesProps>(({$hasError}) => {
    const errorStyles = css`
        &,
        &:focus {
            border-color: ${colors.border.error};
            border-left: none;
        }

        [data-state='expanded'] & {
            border-bottom: none;
        }
    `

    return css`
        /**
         * For some reason, after building the project, the order of styles changed and
         * these styles where no longer taking precedence and showing up. It seemed like
         * the TextInput styles (which we extend here) were overriding these.
         */
        && {
            padding-left: ${tokens.spacing12};
            border-top-left-radius: 0;
            border-bottom-left-radius: 0;
            ${$hasError && errorStyles}
        }
    `
})

interface CountrySelectButtonProps {
    $hasError: boolean
}

const CountrySelectButton = styled(SelectTrigger)<CountrySelectButtonProps>(({$hasError}) => {
    const errorStyles = css`
        &,
        [data-state='expanded'] & {
            border-color: ${colors.border.error};
            border-right: none;
        }

        [data-state='expanded'] & {
            border-bottom: none;
        }
    `

    return css`
        display: grid;
        column-gap: ${tokens.spacing6};
        grid-template-columns: auto auto auto;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;

        &:focus,
        &[data-state='open'] {
            ${focusStyles()}
            z-index: ${zIndex.phoneInputCountrySelect};
        }

        ${$hasError && errorStyles}
    `
})

const CountryOption = styled(SelectOption)`
    display: grid;
    grid-template-columns: auto 1fr auto;
    column-gap: ${tokens.spacing8};
`

const CallingCode = styled.span`
    color: ${colors.text.lightMuted};
`

const OptionCountryName = styled(ItemText)`
    display: block;
    word-break: initial;

    ${ellipsisStyles}
`

const Flag = styled.span`
    ${text({$variant: 'paragraph-large'})}
    display: flex;
    line-height: 0;
    align-items: center;
`

const SelectSeparator = styled(Separator)`
    height: 1px;
    background-color: ${colors.border.default};
    margin: 0;
`
export default PhoneInput
